import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { Subject, forkJoin } from 'rxjs';
import { debounceTime, groupBy, map, mergeMap, takeUntil } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import * as moment from 'moment';
import {
  MobileFrequenciaAulaService,
  DiaAlocacao,
  AlocacaoAula,
  FrequenciaAula,
  FrequenciaAulaComputada,
  EnturmacaoFrequencia,
  FrequenciaAulaRequest
} from '../../services/mobile-frequencia-aula.service';
import { TurmaCompleta } from '../../services/mobile-frequencia.service';
import { HoraAulaService } from '../../services/hora-aula.service';
import { MobileContextService } from '@core/context/services/mobile-context.service';
import { ItemEducacionalColorService } from '@core/context/services/item-educacional-color.service';
import { ordenarPorNomeOuChamada } from '@shared/utils';

const DISPENSADO = 'DISPENSADO';
const DISPENSADO_PARCIAL = 'DISPENSADO_PARCIAL';

@Component({
  selector: 'app-mobile-frequencia-aula',
  templateUrl: './mobile-frequencia-aula.component.html',
  styleUrls: ['./mobile-frequencia-aula.component.css']
})
export class MobileFrequenciaAulaComponent implements OnInit, OnDestroy, OnChanges {
    /**
     * Tipo de registro de frequência da turma: 'TOTAL_FALTAS_AULAS' ou 'TOTAL_FALTAS_DIA'.
     * Necessário para alinhar regra de exibição de mensagens com AngularJS.
     */
    @Input() tipoRegistroFrequencia = 'TOTAL_FALTAS_AULAS';
  @Input() turmaId!: number;
  @Input() periodoId!: number;
  @Input() itemEducacionalIds: number[] = [];
  @Input() dataSelecionada!: string;
  @Input() periodoFechado = false;
  @Input() enturmacoesInput: EnturmacaoFrequencia[] = [];
  /** Sinalizado pelo pai após carregarEnturmacoesAula() concluir (sucesso ou erro).
   *  Impede que "Nenhum aluno encontrado" apareça antes da API ter respondido. */
  @Input() enturmacoesCarregadas = false;
  @Input() aulasCabecalhoInput: DiaAlocacao[] = [];
  @Input() frequenciasAulasInput: FrequenciaAula[] = [];
  @Input() aulaRealizada = false;
  @Input() canOpenAbonoJustificativa = false;
  /** Turma enriquecida (GET /context/turma): multiturno + horaAula — o contexto PWA costuma não trazer estes campos. */
  @Input() turmaFrequenciaMeta: TurmaCompleta | null = null;
  @Output() salvo = new EventEmitter<void>();
  @Output() abonoSolicitado = new EventEmitter<EnturmacaoFrequencia>();
  @Output() faltaAdicionada = new EventEmitter<void>();
  @Output() presencaIndividual = new EventEmitter<void>();

  aulasCabecalho: DiaAlocacao[] = [];
  aulasDoDia: AlocacaoAula[] = [];
  enturmacoes: EnturmacaoFrequencia[] = [];
  frequenciasAulas: FrequenciaAula[] = [];
  
  loading = false;
  errorMessage: string | null = null;
  warningMessage: string | null = null;
  warningMessageDetail: string | null = null;

  tipoOrdenacao: 'alfabetica' | 'numeroChamada' = 'numeroChamada';
  direcaoOrdenacao: 'asc' | 'desc' = 'asc';
  ordenacaoDropdownAberto = false;

  private destroy$ = new Subject<void>();
  private saveQueue = new Subject<{ 
    frequencia: FrequenciaAulaComputada; 
    enturmacao: EnturmacaoFrequencia 
  }>();

  turma: any;
  periodo: any;
  isTurmaAtividade = false;
  isEjaModular = false;
  private horaAulaLoading = false;

  constructor(
    private frequenciaAulaService: MobileFrequenciaAulaService,
    private contextService: MobileContextService,
    private colorService: ItemEducacionalColorService,
    private horaAulaService: HoraAulaService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeSaveQueue();
    this.loadContext();
    void this.ensureHoraAulaLoaded();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // dataSelecionada muda ao navegar entre dias: reprocessa o filtro mesmo sem novos dados do servidor.
    const dadosMudaram =
      changes['aulasCabecalhoInput'] ||
      changes['frequenciasAulasInput'] ||
      changes['enturmacoesInput'] ||
      changes['dataSelecionada'] ||
      changes['turmaFrequenciaMeta'] ||
      changes['turmaId'] ||
      changes['periodoId'];

    if (dadosMudaram) {
      this.processarDadosRecebidos();
    }

    if (changes['turmaFrequenciaMeta'] || changes['turmaId']) {
      void this.ensureHoraAulaLoaded();
    }
    // Cada aula tem seu próprio aula.realizada do backend; não há checkbox geral de "aula realizada" aqui.
  }

  /**
   * Carrega "hora-aula" (quadro HoraAulaService.getList do AngularJS) quando a turma é multiturno.
   * Isso permite validar corretamente turno do aluno (manhã vs tarde) na marcação de falta/presença.
   */
  private async ensureHoraAulaLoaded(): Promise<void> {
    const turmaMeta = this.getTurmaComTurnoMeta();
    if (!turmaMeta?.multiturno) {
      return;
    }

    const itensHoraAula = turmaMeta?.horaAula?.itens;
    if (Array.isArray(itensHoraAula) && itensHoraAula.length > 0) {
      return;
    }

    if (this.horaAulaLoading) {
      return;
    }

    if (!this.turmaId) {
      return;
    }

    this.horaAulaLoading = true;
    try {
      const horaAula = await this.horaAulaService.getHoraAulasTurma(this.turmaId);
      if (!horaAula?.itens?.length) {
        return;
      }

      const base = this.turmaFrequenciaMeta ?? (this.turma ? { id: this.turmaId } : { id: this.turmaId });
      this.turmaFrequenciaMeta = {
        ...(base as TurmaCompleta),
        multiturno: true,
        horaAula: {
          ...(base as any)?.horaAula,
          itens: horaAula.itens
        }
      };

      // Reprocessa para aplicar validação de turno com dados reais.
      if (this.aulasCabecalhoInput?.length && this.enturmacoesInput?.length && this.dataSelecionada) {
        this.processarDadosRecebidos();
      }
    } finally {
      this.horaAulaLoading = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Processa dados recebidos via @Input, sem fazer requisições. */
  private processarDadosRecebidos(): void {
    this.warningMessage = null;
    this.warningMessageDetail = null;

    // Sem alunos, não exibe warningMessage (apenas o empty state).
    if (!this.enturmacoesInput || this.enturmacoesInput.length === 0) {
      this.enturmacoes = [];
      return;
    }

    if (this.dataSelecionada) {
      this.aulasCabecalho = (this.aulasCabecalhoInput || []).filter(diaAula => {
        return diaAula.data === this.dataSelecionada;
      });
    } else {
      this.aulasCabecalho = this.aulasCabecalhoInput || [];
    }
    
    this.frequenciasAulas = this.frequenciasAulasInput || [];

    if (this.aulasCabecalho.length > 0) {
      this.processarAulas();
    }

    if (this.enturmacoesInput.length > 0) {
      this.processarEnturmacoes();

      // Equivalente ao setPeriodosDispensaSeDispensaParcial() do AngularJS.
      this.fetchPeriodosDispensaParcial();

      this.cdr.detectChanges();
    }

    // Evita mostrar aviso durante carregamento inicial ou transições.
    if (!this.enturmacoesInput || !this.dataSelecionada) {
      return;
    }
    
    // Mostra mensagem de aviso apenas para TOTAL_FALTAS_AULAS, igual AngularJS
    const temAlunosProcessados = this.enturmacoes && this.enturmacoes.length > 0;
    // Evita falso aviso com dados obsoletos do mês anterior: só considera "sem aulas"
    // quando aulasCabecalhoInput está vazio (resposta real da API sem quadro) OU quando
    // já contém dados para a data selecionada (resposta atualizada do novo mês) mas não
    // gerou aulas. Enquanto aulasCabecalhoInput ainda tiver dados de outro mês/data, a
    // ausência de aulasDoDia é artefato de transição e não deve disparar o aviso.
    const aulasCabecalhoInputVazio = (this.aulasCabecalhoInput?.length ?? 0) === 0;
    const aulasCabecalhoInputTemDadosParaData = this.dataSelecionada
      ? (this.aulasCabecalhoInput || []).some(d => d.data === this.dataSelecionada)
      : false;
    const semAulasProcessadas =
      this.aulasDoDia.length === 0 &&
      (aulasCabecalhoInputVazio || aulasCabecalhoInputTemDadosParaData);

    if (
      this.tipoRegistroFrequencia === 'TOTAL_FALTAS_AULAS' &&
      temAlunosProcessados &&
      semAulasProcessadas
    ) {
      // Sem aulas — igual AngularJS (faltaAulaEmpty: !vm.aulasCabecalho.length)
      this.warningMessage = 'Não há quadro de horários disponível ou aulas alocadas para esta turma. <b>Entre em contato com a secretaria escolar</b> para disponibilizá-lo.';
      this.warningMessageDetail = 'Somente após a disponibilização do quadro de horários você poderá registrar a frequência diária.';
    }
  }

  private loadContext(): void {
    const context = this.contextService.getContextoAtual();
    this.turma = context?.turma;
    this.periodo = context?.periodo;

    // Mesma lógica do AngularJS.
    this.isTurmaAtividade = (
      this.turmaFrequenciaMeta?.tipoAtendimento === 'ATIVIDADE_COMPLEMENTAR' ||
      this.turmaFrequenciaMeta?.tipoAtendimento === 'ATENDIMENTO_EDUCACIONAL_ESPECIALIZADO'
    ) || false;
    this.isEjaModular = this.turma?.modalidade === 'EJA_MODULAR' || false;
  }

  private initializeSaveQueue(): void {
    this.saveQueue.pipe(
      groupBy(({ frequencia, enturmacao }) =>
        `${enturmacao.id}-${frequencia.alocacaoAula.numero}`
      ),
      mergeMap(group$ =>
        group$.pipe(debounceTime(500))
      ),
      takeUntil(this.destroy$)
    ).subscribe(({ frequencia, enturmacao }) => {
      this.executeSave(frequencia, enturmacao);
    });
  }

  private processarAulas(): void {
    this.aulasDoDia = [];

    this.aulasCabecalho.forEach(diaAula => {
      diaAula.aulas.forEach(aula => {
        this.aulasDoDia.push({
          ...aula,
          disabled: this.isDataPosterior(aula.data)
        } as any);
      });
    });

    this.aulasDoDia.sort((a, b) => (a.numero ?? 0) - (b.numero ?? 0));
  }

  /** Mesma lógica do geraMiolo() do AngularJS. */
  private processarEnturmacoes(): void {
    this.enturmacoes = this.enturmacoesInput.map((ent: any) => {
      const enturmacao: EnturmacaoFrequencia = {
        id: ent.id,
        aluno: ent.aluno,
        numero: ent.numeroChamada || ent.numero,
        nome: ent.matricula?.nome || '',
        nomeSocial: ent.matricula?.nomeFantasia || ent.matricula?.nomeSocial || null,
        dataInicio: ent.dataInicio,
        dataFim: ent.dataFim,
        turnoFrequenta:
          ent.turnoFrequenta ??
          ent?.enturmacao?.turnoFrequenta ??
          ent?.values?.turnoFrequenta,
        faltasTotais: ent.faltasTotais,
        registroFaltas: ent.registroFaltas,
        situacaoItensEducacionais: ent.situacaoItensEducacionais,
        frequenciasAulas: []
      };

      // Gera registros fake (mesma lógica do AngularJS)
      enturmacao.frequenciasAulas = this.gerarFrequenciasComputadas(enturmacao);

      (enturmacao as any).expandido = false;
      
      return enturmacao;
    });
  }

  /**
   * Gera frequências computadas para um aluno
   * Lógica: 
   * 1. Se existe falta registrada no backend → FALTA (F vermelho)
   * 2. Senão, se aula.realizada === true → PRESENCA (P verde) 
   * 3. Senão → null (P cinza - aula não foi dada ainda)
   */
  private gerarFrequenciasComputadas(enturmacao: EnturmacaoFrequencia): FrequenciaAulaComputada[] {
    const faltasAulas: FrequenciaAulaComputada[] = [];

    this.aulasDoDia.forEach(aula => {
      const frequenciaExistente = this.frequenciasAulas.find(f => 
        f.enturmacao.id === enturmacao.id &&
        f.alocacaoAula.id === aula.id
      );

      const turnosAula = this.getTurnosDaAlocacao(
        aula.data,
        aula.numero,
        aula.turnos
      );
      const turnoAluno = this.normalizeTurno(enturmacao.turnoFrequenta);
      const turmaMeta = this.getTurmaComTurnoMeta();
      let aplicavelTurnoAluno = true;
      if (turmaMeta?.multiturno && turnoAluno) {
        if (turnoAluno === 'INTEGRAL') {
          // Aluno de turno integral frequenta todos os turnos (manhã + tarde + noite)
          aplicavelTurnoAluno = true;
        } else if (turnosAula.length === 0) {
          aplicavelTurnoAluno = false;
        } else {
          aplicavelTurnoAluno = turnosAula.includes(turnoAluno);
        }
      }

      const frequenciaComputada: FrequenciaAulaComputada = {
        id: frequenciaExistente?.id,
        isEnturmado: this.verificaAlunoEnturmado(
          aula.data,
          enturmacao.dataInicio,
          enturmacao.dataFim
        ),
        // Presença só quando aula realizada E aluno pode frequentar esse slot (multiturno + hora-aula).
        tipo: frequenciaExistente
          ? 'FALTA'
          : aula.realizada && aplicavelTurnoAluno
            ? 'PRESENCA'
            : null,
        isChecked: aula.realizada,
        disabled: this.isDataPosterior(aula.data),
        hasDispensa: this.hasDispensa(enturmacao, aula.idItemEducacional),
        data: aula.data,
        enturmacao: enturmacao,
        justificado: frequenciaExistente?.justificado || false,
        abonado: frequenciaExistente?.abonado || false,
        alocacaoAula: {
          id: aula.id,
          data: aula.data,
          inicioAula: aula.inicioAula,
          fimAula: aula.fimAula,
          numero: aula.numero,
          periodoAvaliativo: this.periodo,
          itemEducacional: {
            id: aula.idItemEducacional,
            descricao: aula.descricaoItemEducacional
          },
          ...(aula.turnos ? { turnos: aula.turnos } : {})
        },
        uniqueId: `${enturmacao.id}-${aula.id || aula.numero}-${Date.now()}`
      };

      faltasAulas.push(frequenciaComputada);
    });

    return faltasAulas;
  }

  private verificaAlunoEnturmado(
    dataSelecionada: string,
    dataInicioEnturmacao: string,
    dataFimEnturmacao?: string
  ): boolean {
    const dataMoment = moment(dataSelecionada);
    const inicioMoment = moment(dataInicioEnturmacao);
    
    if (dataFimEnturmacao) {
      const fimMoment = moment(dataFimEnturmacao);
      return dataMoment.isSameOrAfter(inicioMoment) && dataMoment.isSameOrBefore(fimMoment);
    }
    
    return dataMoment.isSameOrAfter(inicioMoment);
  }

  private isDataPosterior(data: string): boolean {
    return moment(data).isAfter(moment(), 'day');
  }

  private hasDispensa(enturmacao: EnturmacaoFrequencia, idItemEducacional: number): boolean {
    if (!enturmacao.situacaoItensEducacionais?.length) {
      return false;
    }

    return enturmacao.situacaoItensEducacionais.some(item => {
      if (item.itemEducacional?.id === idItemEducacional) {
        if (item.situacao === DISPENSADO) {
          return true;
        }
        if (item.situacao === DISPENSADO_PARCIAL) {
          return this.isDispensadoNoPeriodo(item.periodosDispensa || []);
        }
      }
      return false;
    });
  }

  private isDispensadoNoPeriodo(periodos: any[]): boolean {
    return periodos.some(p => p.id === this.periodoId);
  }

  /**
   * Busca períodos de dispensa para alunos com DISPENSADO_PARCIAL.
   * Equivalente ao setPeriodosDispensaSeDispensaParcial() do AngularJS (frequencia.controller.js:212-228).
   */
  private fetchPeriodosDispensaParcial(): void {
    const requests: { obs: ReturnType<MobileFrequenciaAulaService['getPeriodosDispensa']>; item: any }[] = [];

    for (const enturmacao of this.enturmacoes) {
      if (!enturmacao.situacaoItensEducacionais?.length) continue;

      for (const item of enturmacao.situacaoItensEducacionais) {
        if (item.situacao === DISPENSADO_PARCIAL && item.itemEducacional?.id) {
          requests.push({
            obs: this.frequenciaAulaService.getPeriodosDispensa(
              this.turmaId,
              enturmacao.id,
              item.itemEducacional.id
            ),
            item
          });
        }
      }
    }

    if (requests.length === 0) return;

    forkJoin(requests.map(r => r.obs.pipe(
      takeUntil(this.destroy$),
      map(periodos => { r.item.periodosDispensa = periodos; })
    ))).subscribe({
      next: () => {
        this.recalcularDispensas();
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('[fetchPeriodosDispensaParcial] Erro ao buscar períodos de dispensa:', err);
      }
    });
  }

  /** Recalcula hasDispensa após carregar periodosDispensa. */
  private recalcularDispensas(): void {
    for (const enturmacao of this.enturmacoes) {
      for (const freq of enturmacao.frequenciasAulas) {
        freq.hasDispensa = this.hasDispensa(enturmacao, freq.alocacaoAula.itemEducacional.id);
      }
    }
  }

  /**
   * Ciclo de presença/falta:
   * null (cinza P) → PRESENCA (verde P) → FALTA (vermelho F) → PRESENCA → FALTA → ...
   * Após o primeiro clique, nunca volta ao estado cinza (null).
   */
  toggleFalta(frequencia: FrequenciaAulaComputada, enturmacao: EnturmacaoFrequencia): void {
    if (
      frequencia.disabled ||
      !frequencia.isEnturmado ||
      frequencia.hasDispensa ||
      this.periodoFechado ||
      !frequencia.isChecked ||
      !this.isVisibleButtonFalta(frequencia)
    ) {
      return;
    }

    if (frequencia.tipo === null) {
      frequencia.tipo = 'PRESENCA';
    } else if (frequencia.tipo === 'PRESENCA') {
      frequencia.tipo = 'FALTA';
    } else {
      frequencia.tipo = 'PRESENCA';
    }

    this.saveQueue.next({ frequencia, enturmacao });
  }

  /**
   * Executa salvamento:
   * - FALTA: salva o registro
   * - PRESENCA ou null: remove o registro se existir (presença é o estado default)
   */
  private executeSave(frequencia: FrequenciaAulaComputada, enturmacao: EnturmacaoFrequencia): void {
    if (frequencia.tipo === 'FALTA') {
      this.saveFrequencia(frequencia, enturmacao);
      return;
    }

    if (frequencia.id) {
      this.removeFrequencia(frequencia, enturmacao);
      return;
    }

    // PRESENCA sem registro prévio: não faz nada. Alinhado com AngularJS
    // (app-frequencia-por-aula.directive.js linha 324 → $q.when()).
  }

  private saveFrequencia(frequencia: FrequenciaAulaComputada, enturmacao: EnturmacaoFrequencia): void {
    // Só é chamada na transição PRESENCA→FALTA, então o estado anterior é sempre PRESENCA.
    const tipoAnterior: FrequenciaAulaComputada['tipo'] = 'PRESENCA';
    const request = this.montaJson(frequencia, enturmacao);

    this.frequenciaAulaService.savePorAula(request).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        frequencia.id = response.registroFrequenciaAula.id;

        enturmacao.faltasTotais = response.resultadoFrequencia;
        enturmacao.registroFaltas = response.registroFaltas;

        if (response.registroFrequenciaAula.alocacaoAula.id) {
          frequencia.alocacaoAula.id = response.registroFrequenciaAula.alocacaoAula.id;

          const aula = this.aulasDoDia.find(a => a.numero === frequencia.alocacaoAula.numero);
          if (aula) {
            aula.id = response.registroFrequenciaAula.alocacaoAula.id;
          }
        }

        this.faltaAdicionada.emit(); // notifica o pai para desmarcar checkbox "aula realizada"
        this.salvo.emit();
      },
      error: (error) => {
        // Mensagem exibida pelo HttpErrorNotificationInterceptor com a mensagem do backend.
        frequencia.tipo = tipoAnterior;
      }
    });
  }

  getNomeEnturmacao(enturmacao: EnturmacaoFrequencia): string {
    return (
      enturmacao?.nomeSocial ||
      enturmacao?.aluno?.nomeSocial ||
      enturmacao?.aluno?.nomeFantasia ||
      enturmacao?.aluno?.nome ||
      enturmacao?.nome ||
      'Nome não disponível'
    );
  }

  private removeFrequencia(frequencia: FrequenciaAulaComputada, enturmacao: EnturmacaoFrequencia): void {
    if (!frequencia.id) {
      return;
    }

    const idToRemove = frequencia.id;

    this.frequenciaAulaService.removeFrequenciaAula(idToRemove).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        delete frequencia.id;

        enturmacao.faltasTotais = response.resultadoFrequencia;
        enturmacao.registroFaltas = response.registroFaltas;

        this.salvo.emit();
      },
      error: (error) => {
        frequencia.tipo = 'FALTA';
        frequencia.id = idToRemove;
      }
    });
  }

  /** Mesma lógica do AngularJS. */
  private montaJson(
    frequencia: FrequenciaAulaComputada,
    enturmacao: EnturmacaoFrequencia
  ): FrequenciaAulaRequest {
    const request: FrequenciaAulaRequest = {
      enturmacao: {
        id: enturmacao.id
      },
      alocacaoAula: {
        id: frequencia.alocacaoAula.id,
        numero: frequencia.alocacaoAula.numero,
        data: frequencia.alocacaoAula.data,
        inicioAula: frequencia.alocacaoAula.inicioAula,
        fimAula: frequencia.alocacaoAula.fimAula,
        itemEducacional: {
          id: frequencia.alocacaoAula.itemEducacional.id
        },
        turma: {
          id: this.turmaId
        }
      }
    };

    if (!request.alocacaoAula.id) {
      delete request.alocacaoAula.id;
    }

    if (!this.isTurmaAtividade && !this.isEjaModular) {
      request.alocacaoAula.periodoAvaliativo = {
        id: this.periodoId
      };
    }

    return request;
  }

  formatarData(data: string): string {
    return moment(data).format('DD/MM');
  }

  /** Cor da bolinha ao lado do número da aula; serviço mantém cache por ID (cor estável por componente curricular). */
  getCorItemEducacional(idItemEducacional: number): string {
    return this.colorService.getColor(idItemEducacional);
  }

  abrirAbono(enturmacao: EnturmacaoFrequencia, event: Event): void {
    event.stopPropagation(); // evita expandir/recolher o card
    this.abonoSolicitado.emit(enturmacao);
  }

  toggleCardExpansion(enturmacao: EnturmacaoFrequencia): void {
    (enturmacao as any).expandido = !(enturmacao as any).expandido;
  }

  isCardExpandido(enturmacao: EnturmacaoFrequencia): boolean {
    return (enturmacao as any).expandido || false;
  }

  get enturmacoesOrdenadas(): EnturmacaoFrequencia[] {
    if (!this.enturmacoes || this.enturmacoes.length === 0) {
      return [];
    }

    return ordenarPorNomeOuChamada(
      this.enturmacoes,
      enturmacao => enturmacao.numero,
      enturmacao => enturmacao.nome,
      this.tipoOrdenacao,
      this.direcaoOrdenacao
    );
  }

  selecionarOrdenacao(tipo: 'alfabetica' | 'numeroChamada', direcao: 'asc' | 'desc'): void {
    this.tipoOrdenacao = tipo;
    this.direcaoOrdenacao = direcao;
    this.ordenacaoDropdownAberto = false;
  }

  toggleOrdenacaoDropdown(): void {
    this.ordenacaoDropdownAberto = !this.ordenacaoDropdownAberto;
  }

  getIconeOrdenacaoAtual(): string {
    if (this.tipoOrdenacao === 'alfabetica') {
      return this.direcaoOrdenacao === 'asc' ? 'arrow-down-a-z' : 'arrow-down-z-a';
    } else {
      return this.direcaoOrdenacao === 'asc' ? 'arrow-down-0-1' : 'arrow-down-1-0';
    }
  }

  trackByEnturmacaoId(index: number, enturmacao: EnturmacaoFrequencia): number {
    return enturmacao.id;
  }

  trackByUniqueId(index: number, item: FrequenciaAulaComputada): string {
    return item.uniqueId;
  }

  getGridCols(count: number): number {
    return Math.min(count, 6);
  }

  hasEnturmacaoAtiva(enturmacao: EnturmacaoFrequencia): boolean {
    return enturmacao.frequenciasAulas.some(f => f.isEnturmado);
  }

  /**
   * Contexto da turma no PWA costuma ser só id/título; multiturno e hora-aula vêm do GET /context/turma (pai).
   */
  private getTurmaComTurnoMeta(): TurmaCompleta | null | undefined {
    return this.turmaFrequenciaMeta ?? this.turma ?? undefined;
  }

  /**
   * Turnos da aula (VESPERTINO, MATUTINO, …): igual ao AngularJS (hora-aula + duração por nº da aula).
   */
  private getTurnosDaAlocacao(
    data: string,
    numeroAula: number,
    turnosNaAlocacao?: string[]
  ): string[] {
    const diretos = (turnosNaAlocacao || [])
      .map((t: string) => this.normalizeTurno(t))
      .filter((t: string | null): t is string => Boolean(t));
    if (diretos.length > 0) {
      return diretos;
    }

    const turmaMeta = this.getTurmaComTurnoMeta();
    const itensHoraAula: any[] = turmaMeta?.horaAula?.itens || [];
    const itemHoraAula = itensHoraAula.find((item: any) =>
      this.itemHoraAulaMatchesDiaSemana(item, data)
    );
    if (!itemHoraAula) {
      return [];
    }

    const itemDuracao = (itemHoraAula.itensDuracao || []).find((item: any) => {
      const nroAula = item?.nroAula ?? item?.numero;
      return Number(nroAula) === Number(numeroAula);
    });

    return (itemDuracao?.turnos || [])
      .map((t: string) => this.normalizeTurno(t))
      .filter((t: string | null): t is string => Boolean(t));
  }

  private itemHoraAulaMatchesDiaSemana(item: any, dateStr: string): boolean {
    const dias = item?.diasDaSemana;
    if (!Array.isArray(dias) || dias.length === 0) {
      return false;
    }
    const diaEnum = this.getDayOfWeekEnum(dateStr);
    const jsDow = moment(dateStr).day();
    return dias.some((d: unknown) => {
      if (typeof d === 'number') {
        if (d >= 0 && d <= 6) {
          return d === jsDow;
        }
        if (d >= 1 && d <= 7) {
          const mapped = d === 7 ? 0 : d;
          return mapped === jsDow;
        }
      }
      return String(d).toUpperCase() === diaEnum;
    });
  }

  /**
   * Texto para tooltip/aria do placeholder quando o slot não se aplica ao turno do aluno (multiturno).
   */
  getTituloSlotTurnoNaoAplicavel(): string {
    return 'Aluno não frequenta este turno neste horário de aula';
  }

  /**
   * Mantém visível quando já existe falta, para permitir desfazer.
   */
  isVisibleButtonFalta(frequenciaAula: FrequenciaAulaComputada): boolean {
    if (frequenciaAula.$$buttonFaltaHidden) {
      return false;
    }

    const turmaMeta = this.getTurmaComTurnoMeta();
    if (!turmaMeta?.multiturno) {
      return true;
    }

    if (frequenciaAula.tipo === 'FALTA') {
      return true;
    }

    const turnoEnturmacao = this.normalizeTurno(
      (frequenciaAula.enturmacao as EnturmacaoFrequencia)?.turnoFrequenta
    );
    if (!turnoEnturmacao || turnoEnturmacao === 'INTEGRAL') {
      return true;
    }

    const turnosAula = this.getTurnosDaAula(frequenciaAula);
    if (turnosAula.length === 0) {
      return true;
    }

    const visible = turnosAula.includes(turnoEnturmacao);
    frequenciaAula.$$buttonFaltaHidden = !visible;
    return visible;
  }

  private getTurnosDaAula(frequenciaAula: FrequenciaAulaComputada): string[] {
    const aloc = frequenciaAula.alocacaoAula as { turnos?: string[] };
    return this.getTurnosDaAlocacao(
      frequenciaAula.alocacaoAula.data,
      frequenciaAula.alocacaoAula.numero,
      aloc?.turnos
    );
  }

  private getDayOfWeekEnum(date: string): string {
    const day = moment(date).day();
    const daysOfWeek = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
    return daysOfWeek[day] || 'MONDAY';
  }

  private normalizeTurno(turno?: string): string | null {
    if (!turno) {
      return null;
    }
    return String(turno).trim().toUpperCase();
  }

  /** Usa o array local frequenciasAulas (já filtrado por dia): reage reativamente ao toggle P/F. */
  getFaltasDoDia(enturmacao: EnturmacaoFrequencia): number {
    return enturmacao.frequenciasAulas.filter(f => f.tipo === 'FALTA').length;
  }

  getAbonosJustificativasCount(enturmacao: EnturmacaoFrequencia): number {
    const count = enturmacao.frequenciasAulas.filter(f => f.abonado || f.justificado).length;
    return count;
  }

  isAllDispensado(enturmacao: EnturmacaoFrequencia): boolean {
    const aulasVisiveis = enturmacao.frequenciasAulas.filter(f => f.isEnturmado);
    if (aulasVisiveis.length === 0) return false;
    return aulasVisiveis.every(f => f.hasDispensa);
  }

  /**
   * Retorna a classe CSS do badge P/F/D ao lado do nome do aluno
   * Lógica:
   * - Se todas as aulas dispensadas → amarelo (bg-yellow-500) “D”
   * - Se tem faltas → vermelho (bg-red-500) “F”
   * - Se não tem faltas E alguma aula foi realizada → verde (bg-green-500) “P”
   * - Se não tem faltas E nenhuma aula foi realizada → cinza (bg-gray-500) “?”
   */
  getBadgeClass(enturmacao: EnturmacaoFrequencia): string {
    if (this.isAllDispensado(enturmacao)) {
      return 'bg-yellow-500';
    }

    const faltas = this.getFaltasDoDia(enturmacao);

    if (faltas > 0) {
      return 'bg-red-500';
    }

    // Verifica presença registrada (não conta “aula realizada” em slot incompatível com o turno do aluno)
    const algumaPresencaValida = enturmacao.frequenciasAulas.some(
      f => f.tipo === 'PRESENCA' && f.isEnturmado && !f.disabled
    );

    return algumaPresencaValida ? 'bg-green-500' : 'bg-gray-500';
  }
}
